import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { CacheBucket, HttpCacheManager, requestDataChanged, withCache } from '@ngneat/cashew';
import { APPLICATION_STATUSES, constants } from '@edassist/common';
import { EventLogSubject } from './../../models/event-log-subject/event-log-subject.interface';
import { EventLog } from '../../models/event-log/event-log.interface';
import { ParticipantSearchResult } from '../../models/participant/participant-search-result.interface';
import { ParticipantSearchQuery } from '../../models/participant/participant-search-query.interface';
import { Participant, ParticipantResults } from '../../models/participant/participant.interface';
import { HttpParamObject } from '../../utility/http-utility/http-param-object.interface';
import { HttpUtilityService } from '../../utility/http-utility/http-utility.service';
import { Address } from '../../models/participant/address.interface';
import { ParticipantSupervisor, Supervisor, SupervisorResponse } from '../../models/participant/supervisor.interface';
import { ParticipantApplicationHistory } from '../../models/participant/participant-application-history.interface';
import { FilterOptions } from '../../models/filters/filter-options.interface';
import { ParticipantExpenseHistoryQuery } from '../../models/participant/participant-expense-history-query.interface';
import { RulesOutput } from '../../models/rules/rules-output.interface';
import { ParticipantApplicationResult } from '../../models/participant/participant-application-result.interface';
import { ParticipantRepaymentInformation } from '../../models/participant/participant-repayment-information';
import { BenefitPeriodFilterOptions } from '../../models/benefit-period/benefit-period-filter-options.interface';
import { ParticipantRepaymentSetup } from '../../models/participant/participant-repayment-setup';
import {
	ParticipantDetail,
	EducationProfile,
	MicroApps,
	HttpPatchUtilityService,
	EventLogPatch,
	ParticipantException,
	ParticipantExceptionRuleHistoryPage,
	SystemEventLog,
	FlatLoanApplication,
	CourseOfStudy,
	DegreeObjective,
	ProjectedParticipantProgramCompletion,
	ThinParticipant,
	ParticipantAccountSnapshot,
	FlatApplication,
	TaxSummaryQueryParams,
	TaxSummary,
	RetirementEnrollmentHistory,
	CreateParticipantForm,
	Program,
	ApplicationLearningType
} from '@edassist/api';
import { Dependent } from '../../models/dependent/dependent.interface';
import { Operation } from 'fast-json-patch';
import { ParticipantTicketsHistory } from '../../models/participant/tickets-history.interface';
import { ParticipantPaymentPreference } from '../../models/participant/participant-payment-preference.interface';
import { ScholarshipType } from '../../models/application/scholarship-type.interface';

@Injectable({ providedIn: 'root' })
export class ParticipantService {
	$participantData: Observable<Participant>;
	private readonly participantSubject: BehaviorSubject<Participant>;
	private readonly participantEligibilityBucket = new CacheBucket();
	private readonly resourcesEligibilityBucket = new CacheBucket();

	private readonly relativeUrl = 'participants/';
	constructor(
		private readonly http: HttpClient,
		private readonly manager: HttpCacheManager
	) {
		this.participantSubject = new BehaviorSubject<Participant>(undefined);
		this.$participantData = this.participantSubject.asObservable();
	}

	addParticipantAlert(participantId: number, eventLog: EventLog): Observable<EventLog> {
		return this.http.post<EventLog>(`${this.relativeUrl}${participantId.toString()}/event-log`, eventLog);
	}

	getParticipantAlerts(participantId: number): Observable<Array<EventLog>> {
		return this.http.get<Array<EventLog>>(`${this.relativeUrl}${participantId.toString()}/event-log`);
	}

	getParticipantDegreeObjectivesGroup(participantId: number): Observable<Array<any>> {
		return this.http.get<Array<DegreeObjective>>(
			`${this.relativeUrl}${participantId.toString()}/degree-objectives-by-group`,
			HttpUtilityService.toPlainHttpParams({ activeProductId: constants.PRODUCTS.WORKFORCE_EDUCATION_ID })
		);
	}

	getDependentDegreeObjectivesGroup(dependentId: number): Observable<Array<any>> {
		return this.http.get<Array<DegreeObjective>>(
			`dependents/${dependentId.toString()}/degree-objectives-by-group`,
			HttpUtilityService.toPlainHttpParams({ activeProductId: constants.PRODUCTS.WORKFORCE_EDUCATION_ID })
		);
	}

	updateParticipantAlert(
		participantId: number,
		eventLogId: number,
		eventLog: EventLogPatch,
		originalObj: EventLog
	): Observable<EventLog> {
		const patchOperation = HttpPatchUtilityService.convertDataToHttpPatchOperation(originalObj, {
			...originalObj,
			...eventLog
		});

		return this.http.patch<EventLog>(
			`${this.relativeUrl}${participantId.toString()}/event-log/${eventLogId}`,
			patchOperation
		);
	}

	updateParticipantException(exceptionId: number, patchOperations: Array<Operation>): Observable<ParticipantException> {
		return this.http.patch<ParticipantException>(
			`${this.relativeUrl}participant-exceptions/${exceptionId}`,
			patchOperations
		);
	}

	getParticipantAlertSubjects(): Observable<Array<EventLogSubject>> {
		return this.http.get<Array<EventLogSubject>>(`${this.relativeUrl}event-log/subjects`, {
			context: withCache({
				version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
				key: 'getParticipantAlertSubjects'
			})
		});
	}

	getParticipantAlertCategoriesById(subjectId: number): Observable<Array<EventLogSubject>> {
		return this.http.get<Array<EventLogSubject>>(`${this.relativeUrl}event-log/subjects/${subjectId}/categories`, {
			context: withCache({
				version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
				key: `getParticipantAlertCategoriesById-${subjectId}`
			})
		});
	}

	getParticipantMicroApps(participantId: number): Observable<Array<MicroApps>> {
		return this.http.get<Array<MicroApps>>(`${this.relativeUrl}${participantId?.toString()}/micro-applications`, {
			context: withCache({
				version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
				key: `getParticipantMicroApps-${participantId}`
			})
		});
	}

	findParticipant(participantSearchQuery: ParticipantSearchQuery): Observable<ParticipantSearchResult> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams(participantSearchQuery);

		return this.http.get<ParticipantSearchResult>(`${this.relativeUrl}find`, queryParams);
	}

	findParticipantsByAdmin(participantSearchQuery: ParticipantSearchQuery): Observable<ParticipantSearchResult> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams(participantSearchQuery);

		return this.http.get<ParticipantSearchResult>(`${this.relativeUrl}find-by-admin`, queryParams);
	}

	getParticipant(participantId: number): Observable<Participant> {
		return (
			this.http
				.get<Participant>(`${this.relativeUrl}${participantId.toString()}`, {
					context: withCache({
						version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
						key: `getParticipant-${participantId}`
					})
				})
				// tslint:disable-next-line:no-unnecessary-callback-wrapper
				.pipe(catchError((error) => of(error)))
		);
	}

	getThinParticipant(participantId: number): Observable<ThinParticipant> {
		return this.http
			.get<ThinParticipant>(`${this.relativeUrl}thin-participant/${participantId.toString()}`)
			.pipe(catchError((error) => of(error)));
	}

	getProxyParticipant(participantId: number, clientId: number, searchTerm: string): Observable<ParticipantResults> {
		return this.http.get<ParticipantResults>(
			`${this.relativeUrl}lookup?clientId=${clientId.toString()}&participantId=${participantId.toString()}&proxyParticipantsOnly=true&searchQuery=${searchTerm.toString()}`
		);
	}

	getParticipantLoginDetails(participantId: number): Observable<Array<Participant>> {
		return this.http.get<Array<Participant>>(`/impersonation/participant-login-details/${participantId.toString()}`);
	}

	setParticipantBehaviorSubject(participant: Participant): void {
		this.participantSubject.next(participant);
	}

	updateParticipant(participantId: number, participantData: Participant): Observable<Participant> {
		return this.http.put<Participant>(`${this.relativeUrl}${participantId.toString()}`, participantData).pipe(
			tap(() => {
				this.manager.delete(`getParticipant-${participantId}`);
			}),
			catchError((error) => of(error))
		);
	}

	getParticipantAddresses(participantId: number): Observable<Array<Address>> {
		return this.http.get<Array<Address>>(`${this.relativeUrl}${participantId.toString()}/addresses`);
	}

	getParticipantCompletionDates(participantId: number): Observable<Array<ProjectedParticipantProgramCompletion>> {
		return this.http.get<Array<ProjectedParticipantProgramCompletion>>(
			`${this.relativeUrl}${participantId.toString()}/program-projected-completion-dates`
		);
	}

	saveProjectedParticipantProgramCompletion(
		participantProgramCompletion: ProjectedParticipantProgramCompletion
	): Observable<void> {
		return this.http.post<void>(
			`${this.relativeUrl}${participantProgramCompletion.participantID.toString()}/projected-participant-program-completion`,
			participantProgramCompletion
		);
	}

	getProjectedParticipantProgramCompletion(
		participantId: number,
		degreeObjectiveId: number,
		courseOfStudyId: number
	): Observable<Array<ProjectedParticipantProgramCompletion>> {
		return this.http.get<Array<ProjectedParticipantProgramCompletion>>(
			`${this.relativeUrl}${participantId}/projected-participant-program-completion?degreeObjectiveId=${degreeObjectiveId}&courseOfStudyId=${courseOfStudyId}`
		);
	}

	getParticipantDegreeObjectives(participantId: number): Observable<Array<DegreeObjective>> {
		return this.http.get<Array<DegreeObjective>>(`${this.relativeUrl}${participantId.toString()}/degree-objectives`);
	}

	getParticipantCoursesOfStudy(
		participantId: number,
		degreeObjectiveId: number,
		activeProductId?: number
	): Observable<Array<CourseOfStudy>> {
		const params = activeProductId ? { activeProductId } : {};

		return this.http.get<Array<CourseOfStudy>>(
			`${this.relativeUrl}${participantId}/degree-objectives/${degreeObjectiveId}/courses-of-study`,
			{ params }
		);
	}

	updateParticipantAddresses(participantId: number, addresses: Array<Address>): Observable<void> {
		return this.http.put<void>(`${this.relativeUrl}${participantId?.toString()}/addresses`, addresses);
	}

	getParticipantReviewer(participantId: number): Observable<Array<Supervisor>> {
		return this.http
			.get<Array<Supervisor>>(`${this.relativeUrl}${participantId.toString()}/my-supervisors`)
			.pipe(catchError(() => of([])));
	}

	getParticipantAppealReviewer(participantId: number): Observable<Array<Participant>> {
		return this.http
			.get<Array<Participant>>(`${this.relativeUrl}${participantId.toString()}/appeal-reviewers`)
			.pipe(catchError(() => of([])));
	}

	getParticipantEligibility(
		participantId: number,
		activeProductId: number,
		resetCache?: boolean
	): Observable<RulesOutput> {
		if (resetCache) {
			this.manager.delete(this.participantEligibilityBucket);
		}

		return this.http
			.get<RulesOutput>(`${this.relativeUrl}${participantId.toString()}/participantEligibilityCheck`, {
				params: { productTypeId: activeProductId },
				context: withCache({
					bucket: this.participantEligibilityBucket
				})
			})
			.pipe(shareReplay(1));
	}

	getProgramEligibility(participantId: number, resetCache?: boolean, coaching?: boolean): Observable<RulesOutput> {
		if (resetCache) {
			this.manager.delete(this.resourcesEligibilityBucket);
		}
		let params = new HttpParams();
		if (coaching) {
			params = params.append('coaching', true);
		}

		return this.http
			.get<RulesOutput>(`${this.relativeUrl}${participantId.toString()}/programEligibilityCheck`, {
				params,
				context: withCache({
					bucket: this.resourcesEligibilityBucket
				})
			})
			.pipe(shareReplay(1));
	}

	getParticipantApplicationExpenseHistory(
		participantId: number,
		expenseHistoryQuery: ParticipantExpenseHistoryQuery
	): Observable<ParticipantApplicationHistory> {
		return this.http.get<ParticipantApplicationHistory>(
			`${this.relativeUrl}${participantId.toString()}/applications/expense-history`,
			HttpUtilityService.toPlainHttpParams(expenseHistoryQuery)
		);
	}

	getLoanParticipantApplicationExpenseHistory(participantId: number): Observable<Array<FlatLoanApplication>> {
		return this.http.get<Array<FlatLoanApplication>>(
			`${this.relativeUrl}${participantId.toString()}/applications/participant-loan-history`
		);
	}

	getParticipantApplicationExpenseHistoryBenefitPeriodOptions(participantId: number): Observable<Array<FilterOptions>> {
		return this.http.get<Array<FilterOptions>>(
			`${this.relativeUrl}${participantId.toString()}/applications/expense-history/benefit-period-options`
		);
	}

	getParticipantApplicationExpenseHistoryOptionsForLifeTime(participantId: number): Observable<Array<FilterOptions>> {
		return this.http.get<Array<FilterOptions>>(
			`${this.relativeUrl}${participantId.toString()}/applications/expense-history/lifetime-benefit-period-options`
		);
	}

	getParticipantApplicationExpenseHistoryProgramOptions(participantId: number): Observable<Array<FilterOptions>> {
		return this.http.get<Array<FilterOptions>>(
			`${this.relativeUrl}${participantId.toString()}/applications/expense-history/program-options`
		);
	}

	getParticipantApplicationExpenseHistoryStatusOptions(participantId: number): Observable<Array<FilterOptions>> {
		return this.http.get<Array<FilterOptions>>(
			`${this.relativeUrl}${participantId.toString()}/applications/expense-history/status-options`
		);
	}

	getParticipantApplicationExpenseHistorySortOptions(participantId: number): Observable<Array<FilterOptions>> {
		return this.http.get<Array<FilterOptions>>(
			`${this.relativeUrl}${participantId.toString()}/applications/expense-history/sort-options`
		);
	}

	getNetworkDiscountLetter(participantId: number): Observable<HttpResponse<Blob>> {
		return this.http.get<Blob>(`${this.relativeUrl}${participantId.toString()}/generate-network-discount-letter`, {
			observe: 'response',
			responseType: 'blob' as 'json'
		});
	}

	getBulkReviewApplications(
		participantId: number,
		appStatusIds?: Array<APPLICATION_STATUSES>
	): Observable<ParticipantApplicationResult> {
		let params = new HttpParams();
		params = params.append('includeAppeals', 'true');
		if (appStatusIds?.length) {
			params = params.append('appStatusIds', appStatusIds.toString());
		}

		return this.http.get<ParticipantApplicationResult>(`${this.relativeUrl}${participantId}/bulk-tasklist`, { params });
	}

	getReviewApplications(
		participantId: number,
		appStatusIds?: Array<APPLICATION_STATUSES>
	): Observable<ParticipantApplicationResult> {
		let params = new HttpParams();
		params = params.append('includeAppeals', 'true');
		if (appStatusIds?.length) {
			params = params.append('appStatusIds', appStatusIds.toString());
		}

		return this.http.get<ParticipantApplicationResult>(`${this.relativeUrl}${participantId}/tasklist`, { params });
	}

	getParticipantApplications(
		participantId: number,
		appStatusIds?: Array<APPLICATION_STATUSES>,
		index?: number,
		records?: number,
		orderByStatus?: boolean
	): Observable<ParticipantApplicationResult> {
		let params = new HttpParams();
		if (appStatusIds?.length) {
			params = params.append('appStatusIds', appStatusIds.toString());
		}
		if (index) {
			params = params.append('index', index.toString());
		}
		if (records) {
			params = params.append('recordsPerPage', records.toString());
		}
		if (orderByStatus) {
			params = params.append('orderByAppStatusPriority', orderByStatus.toString());
		}

		return this.http.get<ParticipantApplicationResult>(`${this.relativeUrl}${participantId.toString()}/applications`, {
			params
		});
	}

	getParticipantRepaymentInformation(participantId: number): Observable<ParticipantRepaymentInformation> {
		return this.http.get<ParticipantRepaymentInformation>(
			`${this.relativeUrl}${participantId.toString()}/repayment-information`
		);
	}

	getParticipantRepaymentSetup(participantId: number): Observable<ParticipantRepaymentSetup> {
		return this.http
			.get<ParticipantRepaymentSetup>(`${this.relativeUrl}${participantId.toString()}/repayment-setup`)
			.pipe(catchError(() => of(undefined)));
	}

	saveParticipantRepaymentSetup(
		participantId: number,
		repaymentSetup: ParticipantRepaymentSetup
	): Observable<ParticipantRepaymentSetup> {
		return this.http.post<ParticipantRepaymentSetup>(
			`${this.relativeUrl}${participantId.toString()}/repayment-setup`,
			repaymentSetup
		);
	}

	updateParticipantRepaymentSetup(
		participantId: number,
		repaymentId: number,
		repaymentSetup: ParticipantRepaymentSetup
	): Observable<ParticipantRepaymentSetup> {
		return this.http.put<ParticipantRepaymentSetup>(
			`${this.relativeUrl}${participantId.toString()}/repayment-setup/${repaymentId}`,
			repaymentSetup
		);
	}

	getBenefitPeriodFilterOptions(
		queryParams: BenefitPeriodFilterOptions,
		participantId: number
	): Observable<Array<FilterOptions>> {
		return this.http.get<Array<FilterOptions>>(
			`${this.relativeUrl}${participantId.toString()}/applications/benefit-period-filter-options`,
			HttpUtilityService.toPlainHttpParams(queryParams)
		);
	}

	isClientAdminAndSupervisor(participantId: number): Observable<boolean> {
		return this.http.get<boolean>(`${this.relativeUrl}${participantId.toString()}/is-client-admin-and-supervisor`, {
			context: withCache({
				version: constants.LOCAL_STORAGE.API_CACHE.VERSION_V1,
				key: `isClientAdminAndSupervisor-${participantId}`
			})
		});
	}

	dismissPage(participantId: number, pageName: string): Observable<void> {
		return this.http.post<void>(`${this.relativeUrl}${participantId.toString()}/dismiss-page/${pageName}`, {});
	}

	dismissBanner(participantId: number, bannerType: string): Observable<void> {
		return this.http.post<void>(
			`${this.relativeUrl}${participantId.toString()}/dismiss-banner/banner-type/${bannerType}`,
			{}
		);
	}

	getBannerDismissalTime(participantId: number, bannerType: string): Observable<Date> {
		return this.http.get<Date>(
			`${this.relativeUrl}${participantId.toString()}/banner-dismissal-time/${bannerType}`,
			{}
		);
	}

	generateRemittanceForm(participantId: number, repaymentMethod: string): Observable<HttpResponse<Blob>> {
		return this.http.post<Blob>(
			`${this.relativeUrl}${participantId.toString()}/generate-remittance-form`,
			{},
			{
				params: { repaymentMethod },
				responseType: 'blob' as 'json',
				observe: 'response'
			}
		);
	}

	getParticipantDetails(participantId: number): Observable<ParticipantDetail> {
		return this.http
			.get<ParticipantDetail>(`${this.relativeUrl}${participantId.toString()}/details`)
			.pipe(catchError(() => of(undefined)));
	}

	updateParticipantDetails(participantId: number, participantDetail: ParticipantDetail): Observable<ParticipantDetail> {
		return this.http
			.put<ParticipantDetail>(`${this.relativeUrl}${participantId.toString()}/details`, participantDetail)
			.pipe(catchError(() => of(undefined)));
	}

	getParticipantEducationProfile(participantId: number): Observable<EducationProfile> {
		return this.http
			.get<EducationProfile>(`${this.relativeUrl}${participantId.toString()}/educational-profiles`)
			.pipe(catchError(() => of(undefined)));
	}

	getParticipantDependents(participantId: number): Observable<Array<Dependent>> {
		return this.http.get<Array<Dependent>>(`${this.relativeUrl}${participantId.toString()}/dependents`);
	}

	getParticipantExceptions(participantId: number): Observable<Array<ParticipantException>> {
		return this.http.get<Array<ParticipantException>>(
			`${this.relativeUrl}${participantId.toString()}/participant-exceptions`
		);
	}

	addParticipantExceptions(
		participantId: number,
		exceptions: Array<ParticipantException>
	): Observable<Array<ParticipantException>> {
		return this.http.post<Array<ParticipantException>>(
			`${this.relativeUrl}${participantId.toString()}/participant-exceptions`,
			exceptions
		);
	}

	getParticipantExceptionsHistory(
		participantId: number,
		orderDirection = 'DESC',
		orderBy = 'dateModified',
		index = 1,
		recordsPerPage = 10
	): Observable<ParticipantExceptionRuleHistoryPage> {
		return this.http.get<ParticipantExceptionRuleHistoryPage>(
			`${this.relativeUrl}${participantId.toString()}/participant-exceptions-history`,
			{
				params: {
					orderDirection,
					orderBy,
					index: index.toString(),
					recordsPerPage: recordsPerPage.toString()
				}
			}
		);
	}

	getEODToday = (): number => {
		const date = new Date();
		date.setHours(23);
		date.setMinutes(59);
		date.setSeconds(59);

		return date.getTime();
	};

	deleteParticipantException(participantId: number, exceptionId: number): Observable<void> {
		return this.http.delete<void>(
			`${this.relativeUrl}${participantId.toString()}/participant-exceptions/${exceptionId}`
		);
	}

	getSupervisorTasklist(participantId: string): Observable<ParticipantApplicationResult> {
		return this.http.get<ParticipantApplicationResult>(`${this.relativeUrl}${participantId}/tasklist`, {
			context: withCache({
				clearCachePredicate: requestDataChanged
			})
		});
	}

	getParticipantTicketHistory(participantId: string): Observable<Array<ParticipantTicketsHistory>> {
		return this.http
			.get<Array<ParticipantTicketsHistory>>(
				`${this.relativeUrl}${participantId.toString()}/vhd/tickets?productTypeId=${constants.PRODUCTS.PRODUCT_TYPE_TUITION_AND_LOAN.ID}`,
				{
					context: withCache({
						clearCachePredicate: requestDataChanged
					})
				}
			)
			.pipe(catchError(() => of(undefined)));
	}

	getParticipantSuperVisorTable(participantId: number): Observable<SupervisorResponse> {
		return this.http
			.get<SupervisorResponse>(`${this.relativeUrl}${participantId.toString()}/team-members`, {
				context: withCache({
					clearCachePredicate: requestDataChanged
				}),
				params: {
					recordsPerPage: -1
				}
			})
			.pipe(catchError(() => of(undefined)));
	}

	getParticipantSystemEventLogs(participantId: number): Observable<Array<SystemEventLog>> {
		return this.http.get<Array<SystemEventLog>>(`${this.relativeUrl}${participantId.toString()}/system-event-logs`, {
			params: {
				fullResults: true
			},
			context: withCache({
				clearCachePredicate: requestDataChanged
			})
		});
	}

	getPaymentPreference(participantId: number): Observable<ParticipantPaymentPreference> {
		return this.http
			.get<ParticipantPaymentPreference>(`${this.relativeUrl}${participantId.toString()}/payment-preference`)
			.pipe(catchError(() => of(undefined)));
	}

	getParticipantAccountSnapshot(participantId: number, benefitPeriod: string): Observable<ParticipantAccountSnapshot> {
		return this.http.get<ParticipantAccountSnapshot>(
			`${this.relativeUrl}${participantId.toString()}/account-snapshot?benefitPeriodName=${benefitPeriod}`
		);
	}

	updateReviewer(supervisor: ParticipantSupervisor): Observable<void> {
		return this.http.put<void>(`${this.relativeUrl}supervisor`, supervisor);
	}

	getParticipantAllHistory(participantId: number): Observable<Array<FlatApplication>> {
		return this.http.get<Array<FlatApplication>>(`${this.relativeUrl}${participantId.toString()}/all-app-history`);
	}

	getParticipantProgramOptions(participantId: number): Observable<Array<FilterOptions>> {
		return this.http.get<Array<FilterOptions>>(
			`${this.relativeUrl}${participantId.toString()}/applications/program-options`
		);
	}

	getTaxSummary(participantId: number, taxSummaryQueryParams: TaxSummaryQueryParams): Observable<TaxSummary> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams(taxSummaryQueryParams);

		return this.http.get<TaxSummary>(`${this.relativeUrl}${participantId}/tax-summary`, queryParams);
	}

	getApplicationsByTaxYear(participantId: number, taxQueryParams: any): Observable<any> {
		const queryParams: HttpParamObject = HttpUtilityService.toPlainHttpParams(taxQueryParams);

		return this.http.get(`${this.relativeUrl}${participantId}/tax-year-applications`, queryParams);
	}

	isEnrolledForYear(participantId: number): Observable<boolean> {
		return this.http
			.get<boolean>(`${this.relativeUrl}${participantId}/annual-service-enrollments/current-year`)
			.pipe(catchError((error) => of(error)));
	}

	getRetirementEnrollmentData(participantId: number): Observable<Array<RetirementEnrollmentHistory>> {
		return this.http.get<Array<RetirementEnrollmentHistory>>(`${this.relativeUrl}${participantId}/retirement/history`);
	}

	createParticipant(participant: CreateParticipantForm): Observable<Participant> {
		return this.http.post<Participant>('ia-participants', participant);
	}

	getNonDegreeLearningTypes(
		participantId: number,
		courseOfStudyId: number,
		degreeObjectiveId: number
	): Observable<Array<ApplicationLearningType>> {
		let params = new HttpParams();
		params = params.append('courseOfStudyId', courseOfStudyId.toString());
		params = params.append('degreeObjectiveId', degreeObjectiveId.toString());

		return this.http.get<Array<ApplicationLearningType>>(
			`${this.relativeUrl}${participantId.toString()}/learning-types`,
			{ params }
		);
	}

	getEligiblePrograms(
		participantId: number,
		courseOfStudyId: number,
		degreeGroupCode: string,
		degreeObjectiveId: number,
		dependentId?: number,
		learningTypeId?: number
	): Observable<Array<Program>> {
		let params = new HttpParams();
		params = params.append('activeProductId', constants.PRODUCTS.WORKFORCE_EDUCATION_ID.toString());
		params = params.append('courseOfStudyId', courseOfStudyId.toString());
		params = params.append('degreeGroupCode', degreeGroupCode);
		params = params.append('degreeObjectiveId', degreeObjectiveId.toString());
		params = params.append('enabledPrograms', 'true');
		if (dependentId) {
			params = params.append('dependentId', dependentId.toString());
		}
		if (learningTypeId) {
			params = params.append('learningTypeId', learningTypeId.toString());
		}

		return this.http.get<Array<Program>>(`${this.relativeUrl}${participantId.toString()}/eligible-programs`, {
			params
		});
	}

	getGrantScholarshipTypes(): Observable<Array<ScholarshipType>> {
		return this.http.get<Array<ScholarshipType>>(`applications/grants-scholarship/types`);
	}
}
